import { getRouteApi } from "@tanstack/react-router";
import { isActiveClient } from "creso-core/utils";
import { Client } from "creso-core/types";
import { type ReactNode } from "react";
import { useActiveStatusCards } from "./active-client-status";
import { UnderReview } from "./under-review-client-status";
import { ClientStatusMemberDisplay } from "./client-status-layout";
import { LeadClientStatus } from "./lead-client-status";
import { useSetupPendingStatusCards } from "./setup-pending-client-status";
import { StackedStatusCards } from "./stacked-status-cards";

// Cards as data, not JSX — lets callers know the real count up front,
// whether stacking just this client (below) or combined with others (the
// family bar). Hooks run unconditionally (rules of hooks); no-op if unmatched.
export function useClientStatusCards(
  client: Client,
  memberDisplay?: ClientStatusMemberDisplay
): ReactNode[] {
  const currentStatus = client?.status;
  const isLead =
    currentStatus === "LEAD" || currentStatus === "VERIFICATION_PENDING";
  const isSetupPending = currentStatus === "SETUP_PENDING";
  const isUnderReview = currentStatus === "UNDER_REVIEW";
  const isActive = !!currentStatus && isActiveClient(client);

  const setupPendingCards = useSetupPendingStatusCards(
    client,
    memberDisplay,
    isSetupPending
  );
  const activeCards = useActiveStatusCards(client, memberDisplay, isActive);

  if (!currentStatus) return [];

  if (isLead) {
    return [
      <LeadClientStatus
        key={`lead-${client.id}`}
        client={client}
        memberDisplay={memberDisplay}
      />,
    ];
  }

  if (isSetupPending) return setupPendingCards;

  if (isUnderReview) {
    return [
      <UnderReview
        key={`under-review-${client.id}`}
        client={client}
        memberDisplay={memberDisplay}
      />,
    ];
  }

  if (isActive) return activeCards;

  return [];
}

export function ClientStatusForClient({
  client,
  memberDisplay,
  stackExpanded,
}: {
  client: Client;
  memberDisplay?: ClientStatusMemberDisplay;
  stackExpanded?: boolean;
}) {
  const cards = useClientStatusCards(client, memberDisplay);

  return (
    <StackedStatusCards
      className="w-full"
      expanded={stackExpanded}
      hoverToExpand={stackExpanded === undefined}
    >
      {cards}
    </StackedStatusCards>
  );
}

export function ClientStatusComponent() {
  const route = getRouteApi("/(auth)/_headed/clients/$id");
  const { client } = route.useLoaderData();

  return <ClientStatusForClient client={client} />;
}
